import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Keyboard,
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  useWindowDimensions,
  View,
} from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';

import { Fonts } from '@/constants/theme';
import { fetchBillingSummary, type BillingSummary } from '@/lib/billing';
import { fetchClassesForSchool, type ClassListItem } from '@/lib/classes-admin';
import {
  createStudentAccount,
  fetchStudentsForSchool,
  toggleStudentActive,
  updateStudentProfile,
  type StudentListItem,
} from '@/lib/students-admin';
import { useAuth } from '@/providers/auth-provider';

type ModalMode = 'create' | 'edit';

export default function AdminStudentsScreen() {
  const { profile } = useAuth();
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const isDesktop = width >= 960;
  const [students, setStudents] = useState<StudentListItem[]>([]);
  const [classes, setClasses] = useState<ClassListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [billingLoading, setBillingLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [classPickerVisible, setClassPickerVisible] = useState(false);
  const [modalMode, setModalMode] = useState<ModalMode>('create');
  const [selectedStudent, setSelectedStudent] = useState<StudentListItem | null>(null);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rollNumber, setRollNumber] = useState('');
  const [selectedClassId, setSelectedClassId] = useState('');
  const [togglingStudentId, setTogglingStudentId] = useState<string | null>(null);
  const [billingSummary, setBillingSummary] = useState<BillingSummary | null>(null);
  const [billingErrorMessage, setBillingErrorMessage] = useState<string | null>(null);

  const modalTitle = useMemo(
    () => (modalMode === 'create' ? 'Add student' : 'Edit student'),
    [modalMode]
  );

  const selectedClass = useMemo(
    () => classes.find((item) => item.id === selectedClassId) ?? null,
    [classes, selectedClassId]
  );

  const loadRosterData = useCallback(async () => {
    if (!profile?.school_id) {
      setLoading(false);
      return;
    }

    try {
      const [studentRows, classRows] = await Promise.all([
        fetchStudentsForSchool(profile.school_id),
        fetchClassesForSchool(profile.school_id),
      ]);

      setStudents(studentRows);
      setClasses(classRows);
    } catch (error) {
      Alert.alert('Unable to load students', getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [profile?.school_id]);

  const loadBillingData = useCallback(async () => {
    if (!profile?.school_id) {
      setBillingLoading(false);
      return;
    }

    try {
      setBillingLoading(true);
      const summary = await fetchBillingSummary();
      setBillingSummary(summary);
      setBillingErrorMessage(null);
    } catch (error) {
      setBillingSummary(null);
      setBillingErrorMessage(getErrorMessage(error));
    } finally {
      setBillingLoading(false);
    }
  }, [profile?.school_id]);

  useEffect(() => {
    void loadRosterData();
    void loadBillingData();
  }, [loadBillingData, loadRosterData]);

  const studentStats = useMemo(() => {
    const total = students.length;
    const active = students.filter((student) => student.is_active).length;
    const inactive = total - active;

    return [
      { label: 'Total', value: String(total) },
      { label: 'Active', value: String(active) },
      { label: 'Inactive', value: String(inactive) },
    ];
  }, [students]);

  const inactiveStudents = useMemo(
    () => students.filter((student) => !student.is_active),
    [students]
  );

  const billingDisplay = useMemo(() => {
    if (billingLoading) {
      return {
        canAddStudent: true,
        compactNote: 'Checking seats...',
        purchasedSeats: 0,
        availableSeats: 0,
        renewalText: 'Loading',
        seatUsageLabel: 'Loading',
      };
    }

    if (!billingSummary) {
      return {
        canAddStudent: false,
        compactNote: billingErrorMessage ?? 'Billing not configured',
        purchasedSeats: 0,
        availableSeats: 0,
        renewalText: 'Not configured',
        seatUsageLabel: '0 / 0 active',
      };
    }

    const renewalText = billingSummary.renewal_anchor_date
      ? formatBillingDate(billingSummary.renewal_anchor_date)
      : 'Not set';

    const compactNote = billingSummary.in_grace_period
      ? `Grace active until renewal support clears payment.`
      : billingSummary.failure_reason ??
        `${billingSummary.active_students} of ${billingSummary.purchased_seats} seats active`;

    return {
      canAddStudent: billingSummary.can_activate_student,
      compactNote,
      purchasedSeats: billingSummary.purchased_seats,
      availableSeats: billingSummary.available_seats,
      renewalText,
      seatUsageLabel: `${billingSummary.active_students} / ${billingSummary.purchased_seats} active`,
    };
  }, [billingErrorMessage, billingLoading, billingSummary]);

  const handleOpenCreateModal = () => {
    if (!billingDisplay.canAddStudent) {
      Alert.alert('Student seats unavailable', billingDisplay.compactNote);
      return;
    }

    openCreateModal();
  };

  const classDistribution = useMemo(() => {
    const counts = new Map<string, number>();

    for (const student of students) {
      counts.set(student.class_id, (counts.get(student.class_id) ?? 0) + 1);
    }

    return classes
      .map((item) => ({
        ...item,
        count: counts.get(item.id) ?? 0,
      }))
      .sort((left, right) => right.count - left.count);
  }, [classes, students]);

  const resetForm = () => {
    setSelectedStudent(null);
    setModalMode('create');
    setFullName('');
    setEmail('');
    setPassword('');
    setRollNumber('');
    setSelectedClassId('');
  };

  const openCreateModal = () => {
    resetForm();
    setModalVisible(true);
  };

  const openEditModal = (student: StudentListItem) => {
    setSelectedStudent(student);
    setModalMode('edit');
    setFullName(student.full_name || '');
    setEmail(student.email);
    setPassword('');
    setRollNumber(student.roll_number);
    setSelectedClassId(student.class_id);
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
    setClassPickerVisible(false);
    resetForm();
  };

  const handleCreateStudent = async () => {
    if (!profile?.school_id) {
      Alert.alert('School missing', 'Your account is missing school access.');
      return;
    }

    if (!fullName.trim() || !email.trim() || !password || !rollNumber.trim() || !selectedClassId) {
      Alert.alert('Missing details', 'Complete all fields to add a student.');
      return;
    }

    setSubmitting(true);

    try {
      await createStudentAccount({
        classId: selectedClassId,
        email,
        fullName,
        password,
        rollNumber,
        schoolId: profile.school_id,
      });

      closeModal();
      await Promise.all([loadRosterData(), loadBillingData()]);
      Alert.alert('Student added', 'The student account has been created.');
    } catch (error) {
      Alert.alert('Student creation failed', getErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditStudent = async () => {
    if (!profile?.school_id || !selectedStudent) {
      Alert.alert('Student missing', 'Select a student first.');
      return;
    }

    if (!fullName.trim() || !rollNumber.trim() || !selectedClassId) {
      Alert.alert('Missing details', 'Enter name, roll number, and class.');
      return;
    }

    setSubmitting(true);

    try {
      await updateStudentProfile({
        classId: selectedClassId,
        fullName,
        profileId: selectedStudent.profile_id,
        rollNumber,
        schoolId: profile.school_id,
        studentId: selectedStudent.id,
      });

      closeModal();
      await loadRosterData();
      Alert.alert('Student updated', 'The student details were updated.');
    } catch (error) {
      Alert.alert('Update failed', getErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStudent = async (student: StudentListItem) => {
    if (!profile?.school_id) {
      Alert.alert('School missing', 'Your account is missing school access.');
      return;
    }

    setTogglingStudentId(student.id);

    try {
      await toggleStudentActive({
        isActive: !student.is_active,
        profileId: student.profile_id,
        schoolId: profile.school_id,
      });

      await Promise.all([loadRosterData(), loadBillingData()]);
    } catch (error) {
      Alert.alert('Status update failed', getErrorMessage(error));
    } finally {
      setTogglingStudentId(null);
    }
  };

  const handleSubmit = () => {
    if (modalMode === 'create') {
      void handleCreateStudent();
      return;
    }

    void handleEditStudent();
  };

  const classPickerSheet = (
    isDesktop ? (
      <Modal
        animationType="fade"
        transparent
        visible={classPickerVisible}
        onRequestClose={() => setClassPickerVisible(false)}>
        <View style={styles.desktopPickerBackdrop}>
          <Pressable
            style={StyleSheet.absoluteFill}
            onPress={() => {
              Keyboard.dismiss();
              setClassPickerVisible(false);
            }}
          />
          <View style={styles.desktopPickerCard}>
            <View style={styles.pickerHeader}>
              <Text style={styles.pickerTitle}>Select class</Text>
              <Pressable
                onPress={() => {
                  Keyboard.dismiss();
                  setClassPickerVisible(false);
                }}
                style={styles.desktopPickerClose}>
                <MaterialIcons name="close" size={24} color="#173D22" />
              </Pressable>
            </View>
            <ScrollView contentContainerStyle={styles.pickerList}>
              {classes.map((item) => (
                <Pressable
                  key={item.id}
                  onPress={() => {
                    Keyboard.dismiss();
                    setSelectedClassId(item.id);
                    setClassPickerVisible(false);
                  }}
                  style={[
                    styles.pickerItem,
                    selectedClassId === item.id && styles.pickerItemActive,
                  ]}>
                  <View style={styles.pickerItemInfo}>
                    <Text
                      style={[styles.pickerName, selectedClassId === item.id && styles.pickerTextActive]}>
                      {getClassLabel(item)}
                    </Text>
                    <Text style={styles.pickerMeta}>{item.academic_year}</Text>
                  </View>
                  {selectedClassId === item.id && (
                    <MaterialIcons name="check-circle" size={20} color="#1B5E20" />
                  )}
                </Pressable>
              ))}
            </ScrollView>
          </View>
        </View>
      </Modal>
    ) : null
  );

  const desktopModalSheet = (
    <Modal animationType="fade" transparent visible={modalVisible} onRequestClose={closeModal}>
      <View style={styles.desktopModalBackdrop}>
        <Pressable style={StyleSheet.absoluteFill} onPress={closeModal} />
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          style={styles.desktopModalKeyboard}>
          <View style={styles.desktopModalShell}>
            <View style={styles.desktopModalCard}>
              <ScrollView
                contentContainerStyle={styles.desktopModalScroll}
                keyboardShouldPersistTaps="handled"
                showsVerticalScrollIndicator={false}>
                <View style={styles.desktopModalHeader}>
                  <View>
                    <Text style={styles.desktopModalHeaderLabel}>
                      {modalMode === 'create' ? 'New student' : 'Edit student'}
                    </Text>
                    <Text style={styles.desktopModalHeaderValue}>
                      {modalMode === 'create' ? 'Add student' : 'Update student'}
                    </Text>
                    <Text style={styles.desktopModalHeaderSubtext}>
                      Keep learner details short and easy to manage.
                    </Text>
                  </View>
                  <Pressable onPress={closeModal} style={styles.desktopModalClose}>
                    <MaterialIcons name="close" size={18} color="#163828" />
                  </Pressable>
                </View>

                <View style={styles.desktopModalForm}>
                  <InputField
                    autoCapitalize="words"
                    label="FULL NAME"
                    onChangeText={setFullName}
                    placeholder="e.g. Ariana Das"
                    value={fullName}
                  />
                  <InputField
                    editable={modalMode === 'create'}
                    label="EMAIL ADDRESS"
                    keyboardType="email-address"
                    onChangeText={setEmail}
                    placeholder="e.g. student@school.edu"
                    value={email}
                  />

                  <View style={styles.desktopModalRow}>
                    <View style={{ flex: 1 }}>
                      <InputField
                        label="ROLL NUMBER"
                        onChangeText={setRollNumber}
                        placeholder="e.g. 17"
                        value={rollNumber}
                      />
                    </View>
                    <View style={{ flex: 1.5 }}>
                      <Pressable
                        onPress={() => {
                          Keyboard.dismiss();
                          setClassPickerVisible(true);
                        }}
                        style={styles.desktopField}>
                        <Text style={styles.fieldLabel}>ASSIGN CLASS</Text>
                        <View style={styles.selectInput}>
                          <Text style={selectedClass ? styles.selectValue : styles.selectPlaceholder}>
                            {selectedClass ? getClassLabel(selectedClass) : 'Select class'}
                          </Text>
                          <MaterialIcons name="arrow-drop-down" size={24} color="#1B5E20" />
                        </View>
                      </Pressable>
                    </View>
                  </View>

                  {modalMode === 'create' && (
                    <InputField
                      label="TEMPORARY PASSWORD"
                      onChangeText={setPassword}
                      placeholder="At least 8 characters"
                      secureTextEntry
                      value={password}
                    />
                  )}
                </View>

                <View style={styles.desktopModalActions}>
                  <Pressable onPress={closeModal} style={styles.desktopModalCancel}>
                    <Text style={styles.desktopModalCancelText}>Cancel</Text>
                  </Pressable>
                  <Pressable onPress={handleSubmit} style={styles.desktopModalSave}>
                    {submitting ? (
                      <ActivityIndicator color="#FFFFFF" />
                    ) : (
                      <Text style={styles.desktopModalSaveText}>
                        {modalMode === 'create' ? 'Register student' : 'Save changes'}
                      </Text>
                    )}
                  </Pressable>
                </View>
              </ScrollView>
            </View>
          </View>
        </KeyboardAvoidingView>
      </View>
    </Modal>
  );

  if (isDesktop) {
    return (
      <>
        <View style={styles.desktopPage}>
          <ScrollView
            contentContainerStyle={styles.desktopScroll}
            showsVerticalScrollIndicator={false}
            style={styles.desktopScrollView}>
            <View style={styles.desktopHero}>
              <View style={styles.desktopHeroTop}>
                <View style={styles.desktopHeroCopy}>
                  <Text style={styles.desktopEyebrow}>Admin / Students</Text>
                  <Text style={styles.desktopTitle}>Student structure</Text>
                  <Text style={styles.desktopSubtitle}>
                    Keep rosters, roll numbers, class placement, and active status easy to manage.
                  </Text>
                </View>

                <Pressable
                  disabled={!billingDisplay.canAddStudent}
                  onPress={handleOpenCreateModal}
                  style={[
                    styles.desktopHeaderBtn,
                    !billingDisplay.canAddStudent && styles.desktopHeaderBtnDisabled,
                  ]}>
                  <MaterialIcons name="add" size={20} color="#F4EFE6" />
                  <Text style={styles.desktopHeaderBtnText}>Add student</Text>
                </Pressable>
              </View>

              <View style={styles.desktopHeroPanel}>
                <View style={styles.desktopHeroPanelMain}>
                  <Text style={styles.desktopHeroDate}>Workspace overview</Text>
                  <Text style={styles.desktopHeroHeadline}>Learner records and class placement.</Text>
                  <Text style={styles.desktopHeroBody}>
                    Use the desktop view to scan the roster, check class distribution, and edit
                    student records faster.
                  </Text>
                </View>

                <View style={styles.desktopStatsGrid}>
                  {studentStats.map((item, index) => (
                    <View
                      key={item.label}
                      style={[styles.desktopStatCard, index > 0 ? styles.desktopStatDivider : null]}>
                      <Text style={styles.desktopStatValue}>{item.value}</Text>
                      <Text style={styles.desktopStatLabel}>{item.label}</Text>
                    </View>
                  ))}
                </View>

                <View style={styles.desktopBillingGrid}>
                  <View style={styles.desktopBillingCard}>
                    <Text style={styles.desktopBillingLabel}>Available seats</Text>
                    <Text style={styles.desktopBillingValue}>{String(billingDisplay.availableSeats)}</Text>
                    <Text style={styles.desktopBillingHint}>Ready for new students</Text>
                  </View>
                  <View style={styles.desktopBillingCard}>
                    <Text style={styles.desktopBillingLabel}>Seat usage</Text>
                    <Text style={styles.desktopBillingMeta}>{billingDisplay.seatUsageLabel}</Text>
                    <Text style={styles.desktopBillingHint}>Purchased {billingDisplay.purchasedSeats}</Text>
                  </View>
                  <View style={styles.desktopBillingCard}>
                    <Text style={styles.desktopBillingLabel}>Renewal</Text>
                    <Text style={styles.desktopBillingMeta}>{billingDisplay.renewalText}</Text>
                    <Text style={styles.desktopBillingHint}>{billingDisplay.compactNote}</Text>
                  </View>
                </View>
              </View>
            </View>

            <View style={styles.desktopGrid}>
              <View style={styles.desktopColumnLarge}>
                <View style={styles.desktopSection}>
                  <View style={styles.sectionHeadDesktop}>
                    <View>
                      <Text style={styles.sectionTitleDesktop}>All students</Text>
                      <Text style={styles.sectionCaptionDesktop}>
                        Select a row to edit details or change active status.
                      </Text>
                    </View>
                    <Pressable disabled={!billingDisplay.canAddStudent} onPress={handleOpenCreateModal}>
                      <Text style={styles.linkTextDesktop}>Add student</Text>
                    </Pressable>
                  </View>

                  {loading ? (
                    <View style={styles.desktopLoadingWrap}>
                      <ActivityIndicator color="#163828" />
                    </View>
                  ) : students.length === 0 ? (
                    <View style={styles.desktopEmptyState}>
                      <View style={styles.desktopEmptyIcon}>
                        <MaterialIcons name="group" size={26} color="#163828" />
                      </View>
                      <Text style={styles.desktopEmptyTitle}>No students found</Text>
                      <Text style={styles.desktopEmptyText}>
                        Add the first student to start building the school roster.
                      </Text>
                      <Pressable onPress={handleOpenCreateModal} style={styles.desktopEmptyButton}>
                        <Text style={styles.desktopEmptyButtonText}>Add student</Text>
                      </Pressable>
                    </View>
                  ) : (
                    <View style={styles.desktopListBlock}>
                      {students.map((student, index) => (
                        <Pressable
                          key={student.id}
                          onPress={() => openEditModal(student)}
                          style={[
                            styles.desktopListRow,
                            index < students.length - 1 ? styles.desktopListDivider : null,
                          ]}>
                          <View style={[styles.desktopRowIndex, !student.is_active && styles.desktopRowIndexInactive]}>
                            <Text style={styles.desktopRowIndexText}>
                              {student.full_name?.slice(0, 1).toUpperCase() || 'S'}
                            </Text>
                          </View>

                          <View style={styles.desktopRowCopy}>
                            <Text style={styles.desktopRowTitle} numberOfLines={1}>
                              {student.full_name || 'Student'}
                            </Text>
                            <Text style={styles.desktopRowMeta} numberOfLines={1}>
                              {student.class_label}
                            </Text>
                          </View>

                          <View style={styles.desktopRowRight}>
                            <Pressable
                              onPress={() => void handleToggleStudent(student)}
                              disabled={togglingStudentId === student.id}
                              style={[
                                styles.desktopRowTag,
                                { backgroundColor: student.is_active ? '#E8F5E9' : '#F5F6FA' },
                              ]}>
                              {togglingStudentId === student.id ? (
                                <ActivityIndicator
                                  size="small"
                                  color={student.is_active ? '#1B5E20' : '#8B93A0'}
                                />
                              ) : (
                                <Text
                                  style={[
                                    styles.desktopRowTagText,
                                    { color: student.is_active ? '#1B5E20' : '#8B93A0' },
                                  ]}>
                                  {student.is_active ? 'Active' : 'Inactive'}
                                </Text>
                              )}
                            </Pressable>
                            <Text style={styles.desktopRowCount}>Roll {student.roll_number}</Text>
                          </View>
                        </Pressable>
                      ))}
                    </View>
                  )}
                </View>
              </View>

              <View style={styles.desktopColumnSide}>
                <View style={styles.desktopSection}>
                  <View style={styles.sectionHeadDesktop}>
                    <View>
                      <Text style={styles.sectionTitleDesktop}>Class distribution</Text>
                      <Text style={styles.sectionCaptionDesktop}>
                        Students grouped by class load.
                      </Text>
                    </View>
                  </View>

                  {loading ? (
                    <View style={styles.desktopLoadingWrap}>
                      <ActivityIndicator color="#163828" />
                    </View>
                  ) : classDistribution.length === 0 ? (
                    <View style={styles.desktopEmptyState}>
                      <Text style={styles.desktopEmptyTitle}>No classes yet</Text>
                      <Text style={styles.desktopEmptyText}>
                        Create classes first to assign students into sections.
                      </Text>
                    </View>
                  ) : (
                    <View style={styles.desktopListBlock}>
                      {classDistribution.map((item, index) => (
                        <View
                          key={item.id}
                          style={[
                            styles.desktopListRow,
                            index < classDistribution.length - 1 ? styles.desktopListDivider : null,
                          ]}>
                          <View style={styles.desktopRowIndex}>
                            <Text style={styles.desktopRowIndexText}>
                              {item.name.slice(0, 1).toUpperCase()}
                            </Text>
                          </View>

                          <View style={styles.desktopRowCopy}>
                            <Text style={styles.desktopRowTitle} numberOfLines={1}>
                              {getClassLabel(item)}
                            </Text>
                            <Text style={styles.desktopRowMeta} numberOfLines={1}>
                              {item.academic_year}
                            </Text>
                          </View>

                          <View style={styles.desktopRowRight}>
                            <View style={styles.desktopRowTag}>
                              <Text style={styles.desktopRowTagText}>{item.count} students</Text>
                            </View>
                            <Text style={styles.desktopRowCount}>Assigned</Text>
                          </View>
                        </View>
                      ))}
                    </View>
                  )}
                </View>

                <View style={styles.desktopSection}>
                  <View style={styles.sectionHeadDesktop}>
                    <View>
                      <Text style={styles.sectionTitleDesktop}>Inactive students</Text>
                      <Text style={styles.sectionCaptionDesktop}>
                        Accounts currently paused or disabled.
                      </Text>
                    </View>
                  </View>

                  {loading ? (
                    <View style={styles.desktopLoadingWrap}>
                      <ActivityIndicator color="#163828" />
                    </View>
                  ) : inactiveStudents.length === 0 ? (
                    <View style={styles.desktopEmptyState}>
                      <Text style={styles.desktopEmptyTitle}>All active</Text>
                      <Text style={styles.desktopEmptyText}>
                        Every student account is currently active.
                      </Text>
                    </View>
                  ) : (
                    <View style={styles.desktopListBlock}>
                      {inactiveStudents.map((student, index) => (
                        <Pressable
                          key={student.id}
                          onPress={() => openEditModal(student)}
                          style={[
                            styles.desktopListRow,
                            index < inactiveStudents.length - 1 ? styles.desktopListDivider : null,
                          ]}>
                          <View style={styles.desktopRowIndexInactive}>
                            <Text style={styles.desktopRowIndexText}>
                              {student.full_name?.slice(0, 1).toUpperCase() || 'S'}
                            </Text>
                          </View>

                          <View style={styles.desktopRowCopy}>
                            <Text style={styles.desktopRowTitle} numberOfLines={1}>
                              {student.full_name || 'Student'}
                            </Text>
                            <Text style={styles.desktopRowMeta} numberOfLines={1}>
                              {student.class_label}
                            </Text>
                          </View>

                          <View style={styles.desktopRowRight}>
                            <View style={[styles.desktopRowTag, { backgroundColor: '#F5F6FA' }]}>
                              <Text style={[styles.desktopRowTagText, { color: '#8B93A0' }]}>
                                Inactive
                              </Text>
                            </View>
                            <Text style={styles.desktopRowCount}>Review account</Text>
                          </View>
                        </Pressable>
                      ))}
                    </View>
                  )}
                </View>
              </View>
            </View>
          </ScrollView>
        </View>
        {desktopModalSheet}
        {classPickerSheet}
      </>
    );
  }

  return (
    <View style={styles.container}>
      <SafeAreaView style={styles.safeArea}>
        <ScrollView
          contentContainerStyle={[
            styles.scrollContent,
            { paddingBottom: Math.max(insets.bottom, 20) + 88 },
          ]}
          showsVerticalScrollIndicator={false}>
          <View style={styles.heroShell}>
            <View style={styles.heroPanel}>
              <View style={styles.heroHeaderRow}>
                <Text style={styles.heroKicker}>Admin / Students</Text>
                <Pressable
                  disabled={!billingDisplay.canAddStudent}
                  onPress={handleOpenCreateModal}
                  style={({ pressed }) => [
                    styles.heroButton,
                    !billingDisplay.canAddStudent && styles.heroButtonDisabled,
                    pressed && styles.pressed,
                  ]}>
                  <MaterialIcons name="add" size={18} color="#163828" />
                  <Text style={styles.heroButtonText}>Add student</Text>
                </Pressable>
              </View>

              <View style={styles.heroBody}>
                <Text style={styles.heroTitle}>Manage learner accounts from one screen.</Text>
                <Text style={styles.heroSubtitle}>
                  Keep class placement, roll numbers, and active status easy to scan.
                </Text>
              </View>

              <View style={styles.heroStatsRow}>
                {studentStats.map((item, index) => (
                  <View
                    key={item.label}
                    style={[styles.heroStat, index > 0 ? styles.heroStatDivider : null]}>
                    <Text style={styles.heroStatValue}>{item.value}</Text>
                    <Text style={styles.heroStatLabel}>{item.label}</Text>
                  </View>
                ))}
              </View>

              <View style={styles.billingCompactCard}>
                <View style={styles.billingCompactTop}>
                  <View style={styles.billingCompactMetric}>
                    <Text style={styles.billingCompactLabel}>Available seats</Text>
                    <Text style={styles.billingCompactValue}>{String(billingDisplay.availableSeats)}</Text>
                  </View>
                  <View style={styles.billingCompactMetric}>
                    <Text style={styles.billingCompactLabel}>Seat usage</Text>
                    <Text style={styles.billingCompactMeta}>{billingDisplay.seatUsageLabel}</Text>
                  </View>
                </View>
                <Text style={styles.billingCompactFoot}>
                  Renews on {billingDisplay.renewalText}. {billingDisplay.compactNote}
                </Text>
              </View>
            </View>
          </View>

          <View style={styles.surface}>
            <View style={styles.sectionBlock}>
              <View style={styles.sectionHead}>
                <Text style={styles.sectionTitle}>All Students</Text>
                <Pressable disabled={!billingDisplay.canAddStudent} onPress={handleOpenCreateModal}>
                  <Text style={styles.linkText}>Add student</Text>
                </Pressable>
              </View>

              {loading ? (
                <View style={styles.loadingWrap}>
                  <ActivityIndicator color="#163828" />
                </View>
              ) : students.length === 0 ? (
                <View style={styles.emptyState}>
                  <View style={styles.emptyIcon}>
                    <MaterialIcons name="group" size={28} color="#163828" />
                  </View>
                  <Text style={styles.emptyTitle}>No students found</Text>
                  <Text style={styles.emptyText}>
                    Add the first student to start building the school roster.
                  </Text>
                  <Pressable onPress={handleOpenCreateModal} style={styles.emptyButton}>
                    <Text style={styles.emptyButtonText}>Add student</Text>
                  </Pressable>
                </View>
              ) : (
                <View style={styles.listBlock}>
                  {students.map((student, index) => (
                    <Pressable
                      key={student.id}
                      onPress={() => openEditModal(student)}
                      style={[
                        styles.listRow,
                        index < students.length - 1 ? styles.listDivider : null,
                      ]}>
                      <View style={[styles.rowIndex, !student.is_active && styles.rowIndexInactive]}>
                        <Text style={styles.rowIndexText}>
                          {student.full_name?.slice(0, 1).toUpperCase() || 'S'}
                        </Text>
                      </View>

                      <View style={styles.rowCopy}>
                        <Text style={styles.rowTitle} numberOfLines={1}>
                          {student.full_name || 'Student'}
                        </Text>
                        <Text style={styles.rowMeta} numberOfLines={1}>
                          {student.class_label}
                        </Text>
                      </View>

                      <View style={styles.rowRight}>
                        <Pressable
                          onPress={() => void handleToggleStudent(student)}
                          disabled={togglingStudentId === student.id}
                          style={[
                            styles.rowTag,
                            { backgroundColor: student.is_active ? '#E8F5E9' : '#F5F6FA' },
                          ]}>
                          {togglingStudentId === student.id ? (
                            <ActivityIndicator
                              size="small"
                              color={student.is_active ? '#1B5E20' : '#8B93A0'}
                            />
                          ) : (
                            <Text
                              style={[
                                styles.rowTagText,
                                { color: student.is_active ? '#1B5E20' : '#8B93A0' },
                              ]}>
                              {student.is_active ? 'Active' : 'Inactive'}
                            </Text>
                          )}
                        </Pressable>
                        <Text style={styles.rowCount}>Roll {student.roll_number}</Text>
                      </View>
                    </Pressable>
                  ))}
                </View>
              )}
            </View>
          </View>
        </ScrollView>
      </SafeAreaView>

      <Modal animationType="slide" transparent visible={modalVisible} onRequestClose={closeModal}>
        <View style={styles.modalBackdrop}>
          <KeyboardAvoidingView
            behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
            style={styles.modalKeyboard}>
            <View style={styles.modalCard}>
              <ScrollView
                contentContainerStyle={styles.modalScrollContent}
                keyboardShouldPersistTaps="handled"
                showsVerticalScrollIndicator={false}>
                <View style={styles.modalHandle} />
                <Text style={styles.modalTitle}>{modalTitle}</Text>
                <Text style={styles.modalSubtitle}>
                  Keep learner details short and easy to manage on mobile.
                </Text>

                <InputField
                  autoCapitalize="words"
                  label="FULL NAME"
                  onChangeText={setFullName}
                  placeholder="e.g. Ariana Das"
                  value={fullName}
                />
                <InputField
                  editable={modalMode === 'create'}
                  label="EMAIL ADDRESS"
                  keyboardType="email-address"
                  onChangeText={setEmail}
                  placeholder="e.g. student@school.edu"
                  value={email}
                />
                <View style={styles.modalRow}>
                  <View style={{ flex: 1 }}>
                    <InputField
                      label="ROLL NUMBER"
                      onChangeText={setRollNumber}
                      placeholder="e.g. 17"
                      value={rollNumber}
                    />
                  </View>
                    <View style={{ flex: 1.5 }}>
                      <Pressable
                        onPress={() => {
                          Keyboard.dismiss();
                          setClassPickerVisible((current) => !current);
                        }}
                        style={styles.field}>
                        <Text style={styles.fieldLabel}>ASSIGN CLASS</Text>
                        <View style={styles.selectInput}>
                          <Text style={selectedClass ? styles.selectValue : styles.selectPlaceholder}>
                          {selectedClass ? getClassLabel(selectedClass) : 'Select class'}
                        </Text>
                        <MaterialIcons name="arrow-drop-down" size={24} color="#1B5E20" />
                        </View>
                      </Pressable>
                    </View>
                  </View>

                {!isDesktop && classPickerVisible && (
                  <View style={styles.pickerInlinePanel}>
                    <View style={styles.pickerHeader}>
                      <Text style={styles.pickerTitle}>Select class</Text>
                      <Pressable
                        onPress={() => {
                          Keyboard.dismiss();
                          setClassPickerVisible(false);
                        }}>
                        <MaterialIcons name="close" size={24} color="#173D22" />
                      </Pressable>
                    </View>
                    <ScrollView
                      contentContainerStyle={styles.pickerList}
                      keyboardShouldPersistTaps="handled"
                      nestedScrollEnabled>
                      {classes.map((item) => (
                        <Pressable
                          key={item.id}
                          onPress={() => {
                            Keyboard.dismiss();
                            setSelectedClassId(item.id);
                            setClassPickerVisible(false);
                          }}
                          style={[
                            styles.pickerItem,
                            selectedClassId === item.id && styles.pickerItemActive,
                          ]}>
                          <View style={styles.pickerItemInfo}>
                            <Text
                              style={[
                                styles.pickerName,
                                selectedClassId === item.id && styles.pickerTextActive,
                              ]}>
                              {getClassLabel(item)}
                            </Text>
                            <Text style={styles.pickerMeta}>{item.academic_year}</Text>
                          </View>
                          {selectedClassId === item.id && (
                            <MaterialIcons name="check-circle" size={20} color="#1B5E20" />
                          )}
                        </Pressable>
                      ))}
                    </ScrollView>
                  </View>
                )}

                {modalMode === 'create' && (
                  <InputField
                    label="TEMPORARY PASSWORD"
                    onChangeText={setPassword}
                    placeholder="At least 8 characters"
                    secureTextEntry
                    value={password}
                  />
                )}

                <View style={styles.modalActions}>
                  <Pressable onPress={closeModal} style={styles.cancelBtn}>
                    <Text style={styles.cancelBtnText}>Cancel</Text>
                  </Pressable>
                  <Pressable onPress={handleSubmit} style={styles.saveBtn}>
                    {submitting ? (
                      <ActivityIndicator color="#FFFFFF" />
                    ) : (
                      <Text style={styles.saveBtnText}>
                        {modalMode === 'create' ? 'Register student' : 'Save changes'}
                      </Text>
                    )}
                  </Pressable>
                </View>
              </ScrollView>
            </View>
          </KeyboardAvoidingView>
        </View>
      </Modal>
      {classPickerSheet}
    </View>
  );
}

function InputField(props: React.ComponentProps<typeof TextInput> & { label: string }) {
  return (
    <View style={styles.field}>
      <Text style={styles.fieldLabel}>{props.label}</Text>
      <TextInput
        placeholderTextColor="#A8B9AA"
        style={[styles.input, props.editable === false && styles.inputDisabled]}
        {...props}
      />
    </View>
  );
}

function getClassLabel(item: ClassListItem) {
  return item.section?.trim() ? `${item.name} - ${item.section}` : item.name;
}

function formatBillingDate(value: string) {
  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function getErrorMessage(error: unknown) {
  if (error instanceof Error) return error.message;
  return 'Something went wrong.';
}

const styles = StyleSheet.create({
  desktopPage: {
    flex: 1,
    backgroundColor: '#F4EFE6',
  },
  desktopScrollView: {
    flex: 1,
  },
  desktopScroll: {
    padding: 28,
    gap: 24,
  },
  desktopHero: {
    gap: 18,
  },
  desktopHeroTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 20,
  },
  desktopHeroCopy: {
    gap: 6,
    flex: 1,
    minWidth: 0,
  },
  desktopEyebrow: {
    color: '#6F7D71',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 1.2,
    fontWeight: '800',
  },
  desktopTitle: {
    color: '#163828',
    fontSize: 34,
    lineHeight: 40,
    fontFamily: Fonts.rounded,
    fontWeight: '900',
  },
  desktopSubtitle: {
    color: '#6F7D71',
    fontSize: 14,
    lineHeight: 20,
    maxWidth: 640,
  },
  desktopHeaderBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    backgroundColor: '#163828',
    borderRadius: 18,
    paddingHorizontal: 14,
    paddingVertical: 12,
    minWidth: 190,
  },
  desktopHeaderBtnDisabled: {
    opacity: 0.55,
  },
  desktopHeaderBtnText: {
    color: '#F7F4EE',
    fontSize: 13,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  desktopHeroPanel: {
    backgroundColor: '#103427',
    borderRadius: 28,
    padding: 22,
    gap: 18,
    borderWidth: 1,
    borderColor: 'rgba(244, 239, 230, 0.09)',
  },
  desktopHeroPanelMain: {
    gap: 8,
    maxWidth: 560,
  },
  desktopHeroDate: {
    color: '#BBD0C2',
    fontSize: 11,
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: 1.1,
  },
  desktopHeroHeadline: {
    color: '#F7F4EE',
    fontSize: 28,
    lineHeight: 34,
    fontFamily: Fonts.rounded,
    fontWeight: '900',
  },
  desktopHeroBody: {
    color: '#C4D3C9',
    fontSize: 14,
    lineHeight: 21,
    maxWidth: 620,
  },
  desktopStatsGrid: {
    flexDirection: 'row',
    gap: 12,
  },
  desktopStatCard: {
    flex: 1,
    borderRadius: 18,
    backgroundColor: 'rgba(244, 239, 230, 0.08)',
    borderWidth: 1,
    borderColor: 'rgba(244, 239, 230, 0.1)',
    padding: 16,
  },
  desktopStatDivider: {
    marginLeft: 0,
  },
  desktopStatValue: {
    color: '#F7F4EE',
    fontSize: 28,
    lineHeight: 32,
    fontFamily: Fonts.rounded,
    fontWeight: '900',
  },
  desktopStatLabel: {
    color: '#9EB5A7',
    fontSize: 12,
    marginTop: 4,
    fontWeight: '700',
  },
  desktopBillingGrid: {
    flexDirection: 'row',
    gap: 12,
  },
  desktopBillingCard: {
    flexGrow: 1,
    borderRadius: 18,
    backgroundColor: 'rgba(244, 239, 230, 0.08)',
    borderWidth: 1,
    borderColor: 'rgba(244, 239, 230, 0.1)',
    padding: 16,
    gap: 4,
  },
  desktopBillingLabel: {
    color: '#9EB5A7',
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 0.8,
    textTransform: 'uppercase',
  },
  desktopBillingValue: {
    color: '#F7F4EE',
    fontSize: 26,
    lineHeight: 30,
    fontWeight: '900',
    fontFamily: Fonts.rounded,
  },
  desktopBillingMeta: {
    color: '#F7F4EE',
    fontSize: 14,
    lineHeight: 19,
    fontWeight: '700',
  },
  desktopBillingHint: {
    color: '#AFC3B5',
    fontSize: 11,
    lineHeight: 16,
  },
  desktopGrid: {
    flexDirection: 'row',
    gap: 20,
    alignItems: 'flex-start',
  },
  desktopColumnLarge: {
    flex: 1.35,
    gap: 20,
    minWidth: 0,
  },
  desktopColumnSide: {
    flex: 0.95,
    gap: 20,
    minWidth: 0,
  },
  desktopSection: {
    gap: 14,
  },
  sectionHeadDesktop: {
    gap: 4,
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
  },
  sectionTitleDesktop: {
    color: '#163828',
    fontSize: 20,
    lineHeight: 24,
    fontFamily: Fonts.rounded,
    fontWeight: '800',
  },
  sectionCaptionDesktop: {
    color: '#6F7D71',
    fontSize: 13,
    lineHeight: 18,
  },
  linkTextDesktop: {
    color: '#47624F',
    fontSize: 13,
    fontWeight: '700',
  },
  desktopListBlock: {
    backgroundColor: '#FFFFFF',
    borderRadius: 22,
    overflow: 'hidden',
  },
  desktopListRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 14,
  },
  desktopListDivider: {
    borderBottomWidth: 1,
    borderBottomColor: '#EEF2EA',
  },
  desktopRowIndex: {
    width: 38,
    height: 38,
    borderRadius: 12,
    backgroundColor: '#E8F5E9',
    alignItems: 'center',
    justifyContent: 'center',
  },
  desktopRowIndexInactive: {
    backgroundColor: '#F5F6FA',
  },
  desktopRowIndexText: {
    color: '#163828',
    fontSize: 13,
    fontWeight: '900',
    fontFamily: Fonts.rounded,
  },
  desktopRowCopy: {
    flex: 1,
    gap: 2,
    minWidth: 0,
  },
  desktopRowTitle: {
    color: '#163828',
    fontSize: 15,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  desktopRowMeta: {
    color: '#6F7D71',
    fontSize: 12,
    lineHeight: 16,
  },
  desktopRowRight: {
    alignItems: 'flex-end',
    gap: 4,
  },
  desktopRowTag: {
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  desktopRowTagText: {
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    fontFamily: Fonts.sans,
  },
  desktopRowCount: {
    color: '#6F7D71',
    fontSize: 11,
    fontWeight: '700',
  },
  desktopLoadingWrap: {
    backgroundColor: '#FFFFFF',
    borderRadius: 22,
    paddingVertical: 28,
    alignItems: 'center',
    justifyContent: 'center',
  },
  desktopEmptyState: {
    backgroundColor: '#FFFFFF',
    borderRadius: 22,
    padding: 18,
    alignItems: 'center',
    gap: 8,
  },
  desktopEmptyIcon: {
    width: 48,
    height: 48,
    borderRadius: 16,
    backgroundColor: '#EEF5EE',
    alignItems: 'center',
    justifyContent: 'center',
  },
  desktopEmptyTitle: {
    color: '#163828',
    fontSize: 15,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  desktopEmptyText: {
    color: '#6F7D71',
    fontSize: 13,
    lineHeight: 19,
    textAlign: 'center',
  },
  desktopEmptyButton: {
    alignSelf: 'stretch',
    minHeight: 44,
    borderRadius: 14,
    backgroundColor: '#163828',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 4,
  },
  desktopEmptyButtonText: {
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  desktopField: {
    gap: 8,
  },
  desktopModalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(13, 22, 16, 0.62)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  desktopModalKeyboard: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  desktopModalShell: {
    width: '100%',
    maxWidth: 820,
    borderRadius: 30,
    overflow: 'hidden',
    backgroundColor: '#FFFFFF',
    shadowColor: '#0E2817',
    shadowOpacity: 0.22,
    shadowRadius: 28,
    shadowOffset: { width: 0, height: 18 },
    elevation: 24,
  },
  desktopModalCard: {
    backgroundColor: '#FDFBF6',
    paddingHorizontal: 28,
    paddingVertical: 24,
  },
  desktopModalScroll: {
    gap: 18,
    paddingBottom: 4,
  },
  desktopModalHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 16,
  },
  desktopModalHeaderLabel: {
    color: '#6F7D71',
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 1,
    textTransform: 'uppercase',
    fontFamily: Fonts.sans,
  },
  desktopModalHeaderValue: {
    color: '#163828',
    fontSize: 22,
    lineHeight: 28,
    fontWeight: '900',
    fontFamily: Fonts.rounded,
    marginTop: 2,
  },
  desktopModalHeaderSubtext: {
    color: '#6F7D71',
    fontSize: 13,
    lineHeight: 18,
    marginTop: 4,
    maxWidth: 520,
  },
  desktopModalClose: {
    width: 40,
    height: 40,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E7EEE7',
  },
  desktopModalForm: {
    gap: 16,
  },
  desktopModalRow: {
    flexDirection: 'row',
    gap: 12,
  },
  desktopModalActions: {
    flexDirection: 'row',
    gap: 12,
    paddingTop: 8,
  },
  desktopModalCancel: {
    flex: 1,
    minHeight: 52,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F5F7F5',
    borderWidth: 1,
    borderColor: '#E6ECE6',
  },
  desktopModalCancelText: {
    color: '#3C4A3E',
    fontSize: 13,
    fontWeight: '800',
  },
  desktopModalSave: {
    flex: 1.4,
    minHeight: 52,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#163828',
  },
  desktopModalSaveText: {
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  desktopPickerBackdrop: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundColor: 'rgba(13, 22, 16, 0.42)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  desktopPickerCard: {
    width: '100%',
    maxWidth: 620,
    maxHeight: '80%',
    backgroundColor: '#FFFFFF',
    borderRadius: 28,
    padding: 20,
    gap: 14,
    shadowColor: '#0E2817',
    shadowOpacity: 0.18,
    shadowRadius: 24,
    shadowOffset: { width: 0, height: 16 },
    elevation: 20,
  },
  desktopPickerClose: {
    width: 36,
    height: 36,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F5F8F5',
  },
  container: {
    flex: 1,
    backgroundColor: '#F4EFE6',
  },
  safeArea: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 18,
    paddingTop: 10,
    gap: 18,
  },
  heroShell: {
    gap: 18,
  },
  heroPanel: {
    backgroundColor: '#163828',
    borderRadius: 28,
    padding: 18,
    gap: 18,
  },
  heroHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  heroKicker: {
    color: '#B9CFBE',
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    fontFamily: Fonts.sans,
    flexShrink: 1,
  },
  heroButton: {
    alignSelf: 'flex-start',
    backgroundColor: '#F4EFE6',
    borderRadius: 16,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  heroButtonDisabled: {
    opacity: 0.55,
  },
  heroButtonText: {
    color: '#163828',
    fontSize: 13,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  heroBody: {
    gap: 8,
  },
  heroTitle: {
    color: '#FFFFFF',
    fontSize: 27,
    lineHeight: 33,
    fontWeight: '900',
    fontFamily: Fonts.rounded,
  },
  heroSubtitle: {
    color: '#D4E4D7',
    fontSize: 14,
    lineHeight: 20,
  },
  heroStatsRow: {
    flexDirection: 'row',
    borderRadius: 20,
    overflow: 'hidden',
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
  },
  billingCompactCard: {
    borderRadius: 20,
    padding: 14,
    gap: 10,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.12)',
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
  },
  billingCompactTop: {
    flexDirection: 'row',
    gap: 12,
  },
  billingCompactMetric: {
    flex: 1,
  },
  billingCompactLabel: {
    color: '#BFD3C4',
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 0.8,
    textTransform: 'uppercase',
  },
  billingCompactValue: {
    color: '#FFFFFF',
    fontSize: 20,
    fontWeight: '900',
    fontFamily: Fonts.rounded,
  },
  billingCompactMeta: {
    color: '#FFFFFF',
    fontSize: 13,
    lineHeight: 18,
    fontWeight: '700',
  },
  billingCompactFoot: {
    color: '#C3D6C9',
    fontSize: 11,
    lineHeight: 16,
  },
  heroStat: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 14,
    paddingHorizontal: 10,
    gap: 3,
  },
  heroStatDivider: {
    borderLeftWidth: 1,
    borderLeftColor: 'rgba(255, 255, 255, 0.12)',
  },
  heroStatValue: {
    color: '#FFFFFF',
    fontSize: 20,
    fontWeight: '900',
    fontFamily: Fonts.rounded,
  },
  heroStatLabel: {
    color: '#B9CFBE',
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 1.1,
    textTransform: 'uppercase',
    fontFamily: Fonts.sans,
  },
  surface: {
    gap: 18,
  },
  sectionBlock: {
    gap: 12,
  },
  sectionHead: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  sectionTitle: {
    color: '#163828',
    fontSize: 18,
    fontWeight: '900',
    fontFamily: Fonts.rounded,
  },
  linkText: {
    color: '#6F7D71',
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 1.1,
    textTransform: 'uppercase',
    fontFamily: Fonts.sans,
  },
  listBlock: {
    backgroundColor: '#FFFFFF',
    borderRadius: 22,
    overflow: 'hidden',
  },
  listRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 14,
  },
  listDivider: {
    borderBottomWidth: 1,
    borderBottomColor: '#EEF2EA',
  },
  rowIndex: {
    width: 38,
    height: 38,
    borderRadius: 12,
    backgroundColor: '#E8F5E9',
    alignItems: 'center',
    justifyContent: 'center',
  },
  rowIndexInactive: {
    backgroundColor: '#F5F6FA',
  },
  rowIndexText: {
    color: '#163828',
    fontSize: 13,
    fontWeight: '900',
    fontFamily: Fonts.rounded,
  },
  rowCopy: {
    flex: 1,
    gap: 2,
  },
  rowTitle: {
    color: '#163828',
    fontSize: 15,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  rowMeta: {
    color: '#6F7D71',
    fontSize: 12,
    lineHeight: 16,
  },
  rowRight: {
    alignItems: 'flex-end',
    gap: 4,
  },
  rowTag: {
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  rowTagText: {
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    fontFamily: Fonts.sans,
  },
  rowCount: {
    color: '#6F7D71',
    fontSize: 11,
    fontWeight: '700',
  },
  loadingWrap: {
    backgroundColor: '#FFFFFF',
    borderRadius: 22,
    paddingVertical: 28,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyState: {
    backgroundColor: '#FFFFFF',
    borderRadius: 22,
    padding: 18,
    alignItems: 'center',
    gap: 8,
  },
  emptyIcon: {
    width: 48,
    height: 48,
    borderRadius: 16,
    backgroundColor: '#EEF5EE',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyTitle: {
    color: '#163828',
    fontSize: 15,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  emptyText: {
    color: '#6F7D71',
    fontSize: 13,
    lineHeight: 19,
    textAlign: 'center',
  },
  emptyButton: {
    alignSelf: 'stretch',
    minHeight: 44,
    borderRadius: 14,
    backgroundColor: '#163828',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyButtonText: {
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'flex-end',
  },
  modalKeyboard: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  modalCard: {
    borderTopLeftRadius: 30,
    borderTopRightRadius: 30,
    backgroundColor: '#FFFFFF',
    padding: 22,
    paddingBottom: 40,
    gap: 16,
    maxHeight: '88%',
  },
  modalScrollContent: {
    gap: 16,
    paddingBottom: 4,
  },
  modalHandle: {
    width: 36,
    height: 4,
    borderRadius: 2,
    backgroundColor: '#EDF2EE',
    alignSelf: 'center',
  },
  modalTitle: {
    color: '#163828',
    fontSize: 20,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  modalSubtitle: {
    color: '#6A8570',
    fontSize: 13,
    lineHeight: 19,
    marginTop: -8,
  },
  field: {
    gap: 8,
  },
  fieldLabel: {
    color: '#163828',
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 1,
    fontFamily: Fonts.sans,
  },
  input: {
    minHeight: 52,
    borderRadius: 14,
    borderWidth: 1.5,
    borderColor: '#E4E8E4',
    backgroundColor: '#FAFBFA',
    paddingHorizontal: 14,
    color: '#1C1C1E',
    fontSize: 15,
  },
  inputDisabled: {
    opacity: 0.7,
  },
  modalRow: {
    flexDirection: 'row',
    gap: 12,
  },
  selectInput: {
    minHeight: 52,
    borderRadius: 14,
    borderWidth: 1.5,
    borderColor: '#E4E8E4',
    backgroundColor: '#FAFBFA',
    paddingHorizontal: 14,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  selectPlaceholder: {
    color: '#A8B9AA',
    fontSize: 15,
  },
  selectValue: {
    color: '#1C1C1E',
    fontSize: 15,
  },
  modalActions: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 4,
  },
  cancelBtn: {
    flex: 1,
    minHeight: 52,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F5F7F5',
    borderWidth: 1,
    borderColor: '#E6ECE6',
  },
  cancelBtnText: {
    color: '#3C4A3E',
    fontSize: 12,
    fontWeight: '800',
  },
  saveBtn: {
    flex: 2,
    minHeight: 52,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#163828',
  },
  saveBtnText: {
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '800',
  },
  pickerInlinePanel: {
    backgroundColor: '#FFFFFF',
    borderRadius: 22,
    padding: 16,
    gap: 14,
    borderWidth: 1,
    borderColor: '#E7EEE7',
  },
  pickerCard: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: 26,
    borderTopRightRadius: 26,
    padding: 18,
    gap: 14,
    maxHeight: '72%',
  },
  pickerHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  pickerTitle: {
    color: '#173D22',
    fontSize: 18,
    fontFamily: Fonts.rounded,
    fontWeight: '800',
  },
  pickerList: {
    gap: 10,
    paddingBottom: 12,
  },
  pickerItem: {
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#E5EEE5',
    padding: 14,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  pickerItemActive: {
    backgroundColor: '#E8F5E9',
    borderColor: '#1B5E20',
  },
  pickerItemInfo: {
    flex: 1,
    gap: 2,
  },
  pickerName: {
    color: '#173D22',
    fontSize: 15,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  pickerTextActive: {
    color: '#1B5E20',
  },
  pickerMeta: {
    color: '#6A8570',
    fontSize: 12,
  },
  pressed: {
    opacity: 0.82,
    transform: [{ scale: 0.99 }],
  },
});
